import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';

import { AuthProvider, useAuth } from './context/AuthContext';
import { PrivateRoute } from './components/PrivateRoute';
import { PermissionRoute } from './components/PermissionRoute';
import { Layout } from './components/Layout';
import { getWorkspaceId, setWorkspace, companyService } from './services/api';
import { CompanyLayout } from './components/CompanyLayout';
import { CompanyConsole, CompanyFacebookApp } from './pages/company/CompanyConsole';
import { GroupReport as CompanyGroupReport } from './pages/company/GroupReport';
import { DowLoader } from './components/DowLoader';

// Pages
import { Login } from './pages/Login';
import { PrivacyPolicy } from './pages/public/PrivacyPolicy';
import { TermsAndConditions } from './pages/public/TermsAndConditions';
import { DataDeletion } from './pages/public/DataDeletion';
import { Dashboard } from './pages/Dashboard';
import { Leads } from './pages/Leads';
import { RecycleBin } from './pages/RecycleBin';
import { FunnelReport } from './pages/FunnelReport';
import { LostReport } from './pages/LostReport';
import { TATReport } from './pages/TATReport';
import { GroupReport } from './pages/GroupReport';
import { AddLead } from './pages/AddLead';
import { LeadDetail } from './pages/LeadDetail';
import { EditLead } from './pages/EditLead';
import { Followups } from './pages/Followups';
import { Profile } from './pages/Profile';
import { Users } from './pages/Users';
import { AddUser } from './pages/AddUser';
import { RolePermissions } from './pages/RolePermissions';
import { Salespersons } from './pages/Salespersons';
import { LeadTransfer } from './pages/LeadTransfer';
import { BrandModels } from './pages/settings/BrandModels';
import { FacebookIntegration } from './pages/settings/FacebookIntegration';
import { LeadDistribution } from './pages/settings/LeadDistribution';
import { WhatsAppIntegration } from './pages/settings/WhatsAppIntegration';
import { WhatsAppTemplates } from './pages/settings/WhatsAppTemplates';
import { Backups } from './pages/settings/Backups';
import { EmailIntegration } from './pages/settings/EmailIntegration';
import { GoogleIntegration } from './pages/settings/GoogleIntegration';
import { WebsiteIntegration } from './pages/settings/WebsiteIntegration';
import { CrmIntegrations } from './pages/settings/CrmIntegrations';
import { NotFound } from './pages/NotFound';

// Company users get the full panel. If no workspace is active yet, auto-select
// their first workspace so they land straight in the panel (no extra step).
// Non-company users pass straight through.
function WorkspaceGate({ children }) {
  const { user } = useAuth();
  const needsWorkspace = user?.isCompany && !getWorkspaceId();

  React.useEffect(() => {
    if (!needsWorkspace) return;
    let cancelled = false;
    companyService.getWorkspaces()
      .then(res => {
        if (cancelled) return;
        const list = res.data?.data?.workspaces || [];
        const ws = list.find(w => w.status === 'active') || list[0];
        if (ws) { setWorkspace(ws._id, ws.name); window.location.assign('/dashboard'); }
        else { window.location.assign('/company'); } // no workspaces yet → manage
      })
      .catch(() => { if (!cancelled) window.location.assign('/company'); });
    return () => { cancelled = true; };
  }, [needsWorkspace]);

  if (needsWorkspace) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <DowLoader size="xl" label="Loading workspace" />
      </div>
    );
  }
  return children;
}

// Company-only route (the console).
function CompanyRoute({ children }) {
  const { user } = useAuth();
  if (!user?.isCompany) return <Navigate to="/dashboard" replace />;
  return children;
}

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/data-deletion" element={<DataDeletion />} />

              {/* Company console — company users only, no workspace context */}
              <Route path="/company" element={<PrivateRoute><CompanyRoute><CompanyLayout /></CompanyRoute></PrivateRoute>}>
                <Route index element={<CompanyConsole />} />
                <Route path="facebook" element={<CompanyFacebookApp />} />
                <Route path="group-report" element={<CompanyGroupReport />} />
                <Route path="profile" element={<Profile />} />
              </Route>

              {/* Private routes */}
              <Route path="/" element={<PrivateRoute><WorkspaceGate><Layout /></WorkspaceGate></PrivateRoute>}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />

                {/* Leads — any user with leads.read permission */}
                <Route path="leads" element={<PermissionRoute permission={{ module: 'leads', action: 'read' }}><Leads /></PermissionRoute>} />
                <Route path="leads/recycle-bin" element={<PermissionRoute permission={{ module: 'leads', action: 'read' }}><RecycleBin /></PermissionRoute>} />
                <Route path="reports/funnel" element={<PermissionRoute permission={{ module: 'leads', action: 'read' }}><FunnelReport /></PermissionRoute>} />
                <Route path="reports/lost"   element={<PermissionRoute permission={{ module: 'leads', action: 'read' }}><LostReport /></PermissionRoute>} />
                <Route path="reports/tat"    element={<PermissionRoute permission={{ module: 'leads', action: 'read' }}><TATReport /></PermissionRoute>} />
                <Route path="reports/group"  element={<PermissionRoute permission={{ module: 'leads', action: 'read' }}><GroupReport /></PermissionRoute>} />
                <Route path="leads/:id" element={<PermissionRoute permission={{ module: 'leads', action: 'read' }}><LeadDetail /></PermissionRoute>} />
                <Route path="leads/:id/edit" element={<PermissionRoute permission={{ module: 'leads', action: 'update' }}><EditLead /></PermissionRoute>} />

                {/* Add lead — requires leads.create */}
                <Route path="leads/new" element={<PermissionRoute permission={{ module: 'leads', action: 'create' }}><AddLead /></PermissionRoute>} />

                {/* Lead transfer — super_admin, admin, dealer */}
                <Route path="leads/transfer" element={<PermissionRoute roles={['super_admin', 'admin', 'dealer']}><LeadTransfer /></PermissionRoute>} />

                {/* Follow-ups — any user with followups.read permission */}
                <Route path="followups" element={<PermissionRoute permission={{ module: 'followups', action: 'read' }}><Followups /></PermissionRoute>} />

                {/* Profile — all authenticated users */}
                <Route path="profile" element={<Profile />} />

                {/* User management — super_admin, admin, dealer (dealer can create sales execs) */}
                <Route path="users" element={<PermissionRoute permission={{ module: 'users', action: 'read' }}><Users /></PermissionRoute>} />
                <Route path="users/new" element={<PermissionRoute permission={{ module: 'users', action: 'create' }}><AddUser /></PermissionRoute>} />

                {/* Role & permissions — super_admin and admin only */}
                <Route path="role-permissions" element={<PermissionRoute roles={['super_admin', 'admin']}><RolePermissions /></PermissionRoute>} />

                {/* Salespersons — super_admin, admin, dealer */}
                <Route path="salespersons" element={<PermissionRoute roles={['super_admin', 'admin', 'dealer']}><Salespersons /></PermissionRoute>} />

                {/* Settings — controlled by Role Permissions */}
                <Route path="settings/brands"   element={<PermissionRoute permission={{ module: 'brands',               action: 'view' }}><BrandModels /></PermissionRoute>} />
                <Route path="settings/facebook" element={<PermissionRoute permission={{ module: 'facebook_integration', action: 'view' }}><FacebookIntegration /></PermissionRoute>} />
                <Route path="settings/lead-distribution" element={<PermissionRoute roles={['super_admin']}><LeadDistribution /></PermissionRoute>} />
                <Route path="settings/whatsapp" element={<PermissionRoute permission={{ module: 'whatsapp_integration', action: 'view' }}><WhatsAppIntegration /></PermissionRoute>} />
                <Route path="settings/whatsapp-templates" element={<PermissionRoute permission={{ module: 'whatsapp_integration', action: 'view' }}><WhatsAppTemplates /></PermissionRoute>} />
                <Route path="settings/email" element={<PermissionRoute roles={['super_admin']}><EmailIntegration /></PermissionRoute>} />
                <Route path="settings/google"   element={<PermissionRoute permission={{ module: 'google_integration',   action: 'view' }}><GoogleIntegration /></PermissionRoute>} />
                <Route path="settings/website"  element={<PermissionRoute permission={{ module: 'website_integration',  action: 'view' }}><WebsiteIntegration /></PermissionRoute>} />
                <Route path="settings/backups" element={<PermissionRoute roles={['super_admin']}><Backups /></PermissionRoute>} />
                <Route path="settings/crm"      element={<PermissionRoute roles={['super_admin']}><CrmIntegrations /></PermissionRoute>} />
              </Route>
              
              {/* 404 route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            
            {/* Toast notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#22c55e',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
